import React, { useCallback, useMemo } from "react";
import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { Grid } from "@material-ui/core";
import * as Yup from "yup";

import { ReportJobAction } from "actions/reportJob";
import {
  BulkReportFormProps,
  ModalActions,
} from "components/MultiSaleReports/ManyReportsForm/BulkReportFormCommon";
import {
  ClientAgingFields,
  ClientAgingInitialValues,
} from "components/MultiSaleReports/SingleReportForm/ClientAgingFields";
import { loadClientAgingValues } from "components/MultiSaleReports/SingleReportForm/clientAgingSavedValues";

import { ClientAgingGroupBy, Reports } from "constants/reports";

import { getIsLivestockAgent } from "selectors";

export const DEFAULT_RELATION_TYPE = "stock_agent";

export const validationSchema = Yup.object().shape({
  relationType: Yup.string()
    .nullable()
    .required("Choose a relationship to make one report per"),
  groupBy: Yup.string().test(
    "not-relation-type",
    "Reports split by a relationship type can't also be grouped by the same relationship type",
    (groupBy, context) => groupBy !== context.parent.relationType,
  ),
});

// Business relation keys are also group bys.
type FormValueType = Omit<typeof ClientAgingInitialValues, "groupBy"> & {
  groupBy: string;
  report: string;
  asAtDate: string | null;
  labelIds: string[];
  relationType: string | null;
  relatedBusinessIds: string[];
  outputFormat: string;
};

const { outputFormatOptions } = Reports.ClientAgingReport;

export function CreateClientAgingReportJobsForm(
  props: BulkReportFormProps,
): React.JSX.Element {
  const { reportSlug, onClose } = props;

  const dispatch = useDispatch();
  const isLivestockAgent = useSelector(getIsLivestockAgent);

  const onSubmit = useCallback(
    (values: FormValueType) => {
      dispatch(ReportJobAction.createBulk(values));
      onClose();
    },
    [dispatch, onClose],
  );

  const initialValues: FormValueType = useMemo(() => {
    const savedValues = loadClientAgingValues(
      outputFormatOptions,
      isLivestockAgent,
    );
    const { groupBy }: { groupBy: string } = savedValues;
    return {
      ...savedValues,
      groupBy:
        groupBy === DEFAULT_RELATION_TYPE ? ClientAgingGroupBy.NONE : groupBy,
      report: reportSlug,
      asAtDate: null,
      labelIds: [],
      relationType: DEFAULT_RELATION_TYPE,
      relatedBusinessIds: [],
    };
  }, [isLivestockAgent, reportSlug]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      <Form>
        <Grid container spacing={2}>
          <ClientAgingFields outputFormatOptions={outputFormatOptions} isBulk />
        </Grid>
        <ModalActions onClose={onClose} allowInitialSubmit />
      </Form>
    </Formik>
  );
}
